/**
 * Creature-mesh upgrade-in-place (E2.3.S2) — swaps an enemy entity's placeholder
 * cone+eye for its blockout proxy asynchronously, keeping the entity's `THREE.Group`
 * as the stable anchor. Enemies are resolved client-side BY NAME (resolve.ts):
 * `EntityState` carries no contentId, so there is no self/other distinction — the
 * same path drives every enemy the client can see.
 *
 * On an enemy entering visibility the caller invokes `upgrade(id, group, name)`:
 *   - resolve name → proxy pack+model (miss / collision / unbound ⇒ NO-OP: cone stays),
 *   - `instantiate(model, lod0)` via the shared `PackLoaderCache`,
 *   - on settle REPLACE the placeholder children captured at upgrade time (the cone
 *     body + eye) with the proxy, disposing their geometry/materials in place.
 *
 * Handles, per the story:
 *   - entity despawned before the load settles ⇒ drop the clone, no orphan
 *     (renderer-wide monotonic generation + record-identity guard),
 *   - unknown model / null template / load error ⇒ leave the cone, log warn
 *     (the loader's own null path),
 *   - one proxy per entity incl. swarm (InstancedMesh swarm optimisation = E3.6,
 *     recorded — not taken here),
 *   - lod0 only (entity LOD banding = later polish, recorded).
 *
 * Animation (q-creature-rigging-and-locomotion-clips step 5): when a `createMixer`
 * factory is injected and the settled proxy's artifact carries clips, the proxy is
 * attached to a `RigAnimator` — the same player/NPC animator, clip resolver and
 * movement-wins policy, unchanged. The wire `anim` drives Idle/Walk; combat events
 * drive attack/hit/dead one-shots — and, since AD-636, an attack whose content
 * names a `motion` plays the rig's `Attack_<motion>` clip from its wind-up
 * (`beginAttack`/`landAttack`), falling back to the generic strike. A static proxy (no clips, or no factory) is
 * simply never attached and renders exactly as before. Because the server deletes
 * a killed enemy in the same tick it dies, `retire()` keeps a dying rigged proxy
 * as a short-lived CORPSE so its clamped death clip is seen, then hands it back to
 * the caller to drop from the scene.
 * The flyer hovers by its own mesh pivot — no code special-case. The group's
 * position / facing / raycast and the fixed y=2.1 health bar (Game.ts) are
 * untouched (the 3.30 m sentinel proxy sits above the 2.1 m bar — recorded, not
 * redesigned).
 *
 * Nothing rides the per-tick path — upgrades key off snapshot entity-spawn (AD-031)
 * and the proxy is a BODYLESS render mesh carrying no collision/stats (AD-156; the
 * world descriptor + colliders are untouched, off the per-tick path by construction).
 * Node-safe like loader.ts: `three` is a type-only `Object3D`; the loader is
 * injected and the group is an abstract `EntityGroup`, so the whole lifecycle runs
 * under `node --test` with fakes (AD-120). Import boundary (AD-122): shared types
 * via `@great-cloud/shared/client`; the pack-loader acquisition is CONSUMED from
 * the shared `PackLoaderCache` (E2.2.S2 generalisation), never duplicated.
 */

import type { Object3D } from 'three';

import { PackLoaderCache, disposeObject3D, type GltfLoadFn } from '../assets/index.js';
import type { AnimState } from '@great-cloud/shared/client';

import { RigAnimator, type RigMixerFactory } from '../rig-avatar/animator.js';
import {
  CREATURE_ACTION_POLICY,
  MOTION_ATTACK_POLICY,
  resolveAttackMotionClip,
  type RigAction,
} from '../rig-avatar/clips.js';
import { locomotionSpeedsFrom } from '../rig-avatar/locomotionRate.js';
import { fullDetailAssetsActive } from '../scene/assetDetail.js';
import {
  buildEnemyAttackMotionMap,
  buildEnemyNameMap,
  resolveCreature,
  type CreatureContent,
  type EnemyAttackMotionMap,
  type EnemyNameMap,
} from './resolve.js';

/**
 * The entity's placeholder group (a `THREE.Group` structurally): the renderer reads
 * its current children to replace them and adds the proxy. Abstract so the lifecycle
 * runs under `node --test` with a fake group.
 */
export interface EntityGroup {
  readonly children: readonly Object3D[];
  add(object: Object3D): void;
  remove(object: Object3D): void;
}

/** Everything App hands Game so it can drive enemy creature meshes. */
export interface CreatureBridge {
  content: CreatureContent;
}

export interface CreatureRendererOptions {
  /** Presentation lookup (enemy defs for the reverse map + asset packs). */
  content: CreatureContent;
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /** GPU-resource disposer for a detached mesh (default: three geometry/material dispose). */
  dispose?: (object: Object3D) => void;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /** Override the session fidelity policy (tests and explicit degraded paths). */
  useFullDetailBase?: boolean;
  /**
   * Browser-side mixer factory (`createThreeRigMixer()`). Its presence turns creature
   * animation on; omitted (node tests, degraded paths) ⇒ every proxy stays static.
   */
  createMixer?: RigMixerFactory;
  /** How long a dying rigged proxy lingers as a corpse before `retire`'s callback (ms). */
  corpseLingerMs?: number;
  /**
   * Start with upgrades HELD (q-mobile-first-frame-defer-creatures-and-npc-clips): enemies
   * keep their placeholder and wait in `awaitingContent` until `releaseUpgrades()`, so the
   * low tier's first frame does not wait on creature rigs. Default false (no hold).
   */
  holdUpgrades?: boolean;
}

/** Default corpse linger: the ≤ 2 s authored death clip plus a short held beat. */
export const DEFAULT_CORPSE_LINGER_MS = 3000;

/** Per-entity upgrade record (one proxy per entity; async generation-guarded). */
interface EntityRecord {
  /** Renderer-wide monotonic id; a settled load applies only if the record still holds it. */
  generation: number;
  /** The entity's group (the stable anchor). */
  group: EntityGroup;
  /** The placeholder children captured at upgrade time (cone body + eye) to replace. */
  placeholders: readonly Object3D[];
  /** The settled proxy mesh, or null while loading / on a graceful fallback. */
  object: Object3D | null;
  /** Whether the settled proxy is attached to the animator (it carried usable clips). */
  animated: boolean;
  /** The entity's display name — the key its attacks' motions resolve by (AD-636). */
  name: string | null | undefined;
  /** The settled proxy's clip names (empty until settle / when static). */
  clipNames: readonly string[];
  /**
   * AD-636: a per-attack motion clip is carrying the CURRENT wind-up (started at the
   * `telegraph`), so the resolve must not restart a strike over it, and a flinch
   * must not break it. Cleared when that telegraph lands.
   */
  winding: boolean;
}

/** A dying proxy kept on screen for its death clip; `onGone` fires when it is dropped. */
interface Corpse {
  entityId: number;
  record: EntityRecord;
  remainingMs: number;
  onGone: () => void;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[creatures] ${message}`, error ?? '');
}

/**
 * Drives enemy entities' proxy meshes. Create it once against the content bridge,
 * `upgrade()` each enemy as it enters visibility, `remove()` it when it leaves/dies,
 * and `dispose()` on scene/avatar teardown.
 */
export class CreatureRenderer {
  private readonly content: CreatureContent;
  private readonly packLoaders: PackLoaderCache;
  private readonly disposeObject: (object: Object3D) => void;
  private readonly onWarn: (message: string, error?: unknown) => void;
  private readonly useFullDetailBase: boolean;
  /** Creature animation; `null` when no mixer factory was injected (static proxies). */
  private readonly animator: RigAnimator | null;
  private readonly corpseLingerMs: number;

  /** entityId → upgrade record. */
  private readonly records = new Map<number, EntityRecord>();
  /** Retired dying proxies still playing their death clip. */
  private corpses: Corpse[] = [];
  /** Lazily built once the pack has enemies (Game constructs before the pack loads). */
  private nameMap: EnemyNameMap | null = null;
  /** name → (attackId → motion), lazily built like `nameMap` (AD-636). */
  private attackMotionMap: EnemyAttackMotionMap | null = null;
  /** `content.revision()` both maps were built against; a change drops them. */
  private mapsRevision: string | undefined = undefined;
  /**
   * Enemies that became visible BEFORE the content pack loaded, awaiting their upgrade
   * (2026-09-16). The original recovery was "a later entity retries" — which rescues the
   * LATER entity, never the early one. In an ordinary zone enemies drift into view over time,
   * so that was masked; the Bestiary sends all 81 exhibits in the join snapshot, before the
   * pack has loaded, so every exhibit hit the early return and stayed a placeholder cone for
   * the whole session. Drained by `update()` as soon as the pack has enemies.
   */
  private readonly awaitingContent = new Map<
    number,
    { group: EntityGroup; name: string | null | undefined }
  >();
  /** Renderer-wide monotonic attach id (see EntityRecord.generation). */
  private nextGeneration = 0;
  private disposed = false;
  /** Upgrades parked until `releaseUpgrades()` (the first-frame gate). */
  private held: boolean;
  /**
   * Enemies whose upgrade settled on the placeholder for good (no def / no model / a failed
   * load): the cone is their final state, so the loading screen reports them rather than
   * waiting on them (q-asset-readiness-loading-screen). Cleared by a new attempt or removal.
   */
  private readonly fallbackIds = new Set<number>();

  constructor(options: CreatureRendererOptions) {
    this.content = options.content;
    this.packLoaders = new PackLoaderCache({
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      onWarn: options.onWarn ?? defaultWarn,
    });
    this.disposeObject = options.dispose ?? disposeObject3D;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.useFullDetailBase = options.useFullDetailBase ?? fullDetailAssetsActive();
    this.animator =
      options.createMixer === undefined
        ? null
        : new RigAnimator({
            createMixer: options.createMixer,
            onWarn: options.onWarn,
            actionPolicy: CREATURE_ACTION_POLICY,
          });
    this.corpseLingerMs = options.corpseLingerMs ?? DEFAULT_CORPSE_LINGER_MS;
    this.held = options.holdUpgrades ?? false;
  }

  /** Whether this enemy's upgrade settled on its placeholder for good (see `fallbackIds`). */
  isFallback(entityId: number): boolean {
    return this.fallbackIds.has(entityId);
  }

  /** Lift the first-frame hold: every parked enemy upgrades on the next `update()`. */
  releaseUpgrades(): void {
    this.held = false;
  }

  /**
   * Drop both maps when the content's revision moved (a reconnect onto a new pack hash);
   * they rebuild lazily from the new pack. Settled proxies keep what they resolved to.
   */
  private dropStaleMaps(): void {
    const revision = this.content.revision?.();
    if (revision === this.mapsRevision) return;
    this.mapsRevision = revision;
    this.nameMap = null;
    this.attackMotionMap = null;
  }

  /** Build the name→def map once per pack revision, when the pack has enemies (else defer). */
  private ensureNameMap(): EnemyNameMap | null {
    this.dropStaleMaps();
    if (this.nameMap !== null) return this.nameMap;
    const enemies = this.content.enemies();
    if (enemies.length === 0) return null; // pack not loaded yet — retry on the next enemy
    this.nameMap = buildEnemyNameMap(enemies);
    return this.nameMap;
  }

  /**
   * Upgrade one enemy entity's placeholder to its proxy, asynchronously. A resolve
   * miss (unknown/collided name, unbound def, undelivered pack) or a null template
   * leaves the caller's cone+eye in place (graceful — the fallback stays reachable).
   * Re-calling for an entity that already has an in-flight/settled upgrade is a no-op.
   */
  upgrade(entityId: number, group: EntityGroup, name: string | null | undefined): void {
    if (this.disposed) return;
    // An id comes back while its corpse still lingers (a zone or run change keeps the
    // renderer, and a new zone numbers from its own space): the corpse goes NOW. Left,
    // its expiry would detach the animator key the new enemy's mixer then holds.
    const corpseAt = this.corpses.findIndex((corpse) => corpse.entityId === entityId);
    if (corpseAt >= 0) this.dropCorpse(this.corpses.splice(corpseAt, 1)[0]!);
    if (this.records.has(entityId)) return; // already upgrading/upgraded this entity
    if (this.held) {
      // First-frame hold: the placeholder stays and this entity upgrades on release.
      this.awaitingContent.set(entityId, { group, name });
      return;
    }
    const nameMap = this.ensureNameMap();
    if (nameMap === null) {
      // No content yet ⇒ cone stays FOR NOW, and THIS entity is retried by update() once the
      // pack loads (see awaitingContent) — not left to hope a later entity arrives.
      this.awaitingContent.set(entityId, { group, name });
      return;
    }
    this.awaitingContent.delete(entityId);
    const resolution = resolveCreature(nameMap, this.content, name);
    if (resolution === null) {
      this.fallbackIds.add(entityId); // graceful: cone stays, no error
      return;
    }
    this.fallbackIds.delete(entityId);

    const generation = ++this.nextGeneration;
    const record: EntityRecord = {
      generation,
      group,
      placeholders: [...group.children], // the cone body + eye, captured now
      object: null,
      animated: false,
      name,
      clipNames: [],
      winding: false,
    };
    this.records.set(entityId, record);

    const loader = this.packLoaders.loaderFor(resolution.pack);
    void loader
      .instantiatePreferred(resolution.model, this.useFullDetailBase)
      .then(async (object) => {
        // Clips come from the same cached artifact (no extra request); only read when
        // animation is on, so a static-only session behaves exactly as before.
        const clips =
          object !== null && this.animator !== null
            ? await loader.animationClipsPreferred(resolution.model, this.useFullDetailBase)
            : [];
        return { object, clips };
      })
      .then(({ object, clips }) => {
        const current = this.records.get(entityId);
        if (current !== record || current.generation !== generation || this.disposed) {
          // Superseded (entity despawned / renderer gone) ⇒ dispose the clone, no orphan.
          if (object !== null) this.disposeObject(object);
          return;
        }
        if (object === null) {
          // Unknown model / null template / load error ⇒ leave the cone (graceful).
          this.records.delete(entityId);
          this.fallbackIds.add(entityId);
          return;
        }
        // Replace the placeholder cone+eye with the proxy IN PLACE (group stays anchor).
        for (const child of record.placeholders) {
          group.remove(child);
          this.disposeObject(child);
        }
        group.add(object);
        current.object = object;
        // A proxy with no usable clips is not attached and stays static (never-blank).
        // Authored ground speed per locomotion clip (measured data on the asset entry) —
        // what keeps the feet planted at the enemy's real speed (speed-matched playback).
        const entry = resolution.pack.assets?.find((asset) => asset.id === resolution.model);
        current.animated =
          this.animator?.attach(entityId, object, clips, locomotionSpeedsFrom(entry?.locomotion)) ??
          false;
        current.clipNames = current.animated ? clips.map((clip) => clip.name) : [];
      })
      .catch((error: unknown) => {
        // The loader already degrades load failures to null; guard an unexpected throw
        // so a bad upgrade never breaks the frame loop (the cone stays).
        this.records.delete(entityId);
        this.fallbackIds.add(entityId);
        this.onWarn(`upgrade '${resolution.model}' (entity ${entityId}) failed`, error);
      });
  }

  /**
   * Present the entity's wire locomotion state (`idle`/`move`/`dodge`). Cheap and
   * safe every frame: a repeated state, a static proxy or an unknown id is a no-op.
   */
  setAnimState(entityId: number, state: AnimState): void {
    this.animator?.setState(entityId, state);
  }

  /**
   * Feed the enemy's rendered ground position for this frame (after `setAnimState`):
   * its walk then plays at the rate that keeps its feet planted
   * (`q-locomotion-speed-matched-playback`). A no-op for a static proxy / unknown id.
   */
  observeGround(entityId: number, x: number, z: number, dtMs: number): void {
    this.animator?.observeGround(entityId, x, z, dtMs);
  }

  /**
   * Play a one-shot (`attack` / `hit` / `dead`) on an animated proxy. Returns whether
   * it started — `false` for a static proxy, an unknown id, or a held death.
   */
  playAction(entityId: number, action: RigAction): boolean {
    if (this.animator === null) return false;
    const record = this.records.get(entityId);
    // AD-636: a flinch never breaks a wind-up — players strike an enemy every 0.6 s, so
    // letting `hit` replace the motion clip would erase it in any real melee. Death does.
    // Only while the clip still runs: once it is gone the flinch plays, and `winding` stays
    // set so the resolve still knows the authored strike already happened (no replay).
    const windingClipRuns =
      record?.winding === true && this.animator.activeAction(entityId) === 'attack';
    if (action === 'hit' && windingClipRuns) return false;
    if (action === 'dead' && record !== undefined) record.winding = false;
    return this.animator.playAction(entityId, action);
  }

  /**
   * An enemy begins winding up `attackId` (the wire `telegraph`, AD-636). When its
   * def gives that attack a `motion` AND the settled rig ships the matching
   * `Attack_<motion>[_heavy]` clip, that clip starts NOW at its own rate — it is
   * authored with contact at the served `windupMs`, so the strike lands as the ring
   * fills (docs/content/raid-schema.md §3.1a). Returns whether it started; `false`
   * (static proxy, no attack id, no motion, no clip, a held death) leaves the
   * generic strike to `landAttack`, exactly as before AD-636.
   */
  beginAttack(entityId: number, attackId: string | undefined, heavy: boolean): boolean {
    const record = this.records.get(entityId);
    if (this.animator === null || record === undefined || !record.animated) return false;
    record.winding = false; // a new telegraph supersedes whatever the last one left
    if (attackId === undefined || attackId === '') return false;
    const motion = this.attackMotion(record.name, attackId);
    if (motion === null) return false;
    const clip = resolveAttackMotionClip(motion, heavy, record.clipNames);
    if (clip === null) return false;
    const started = this.animator.playActionClip(entityId, 'attack', clip, MOTION_ATTACK_POLICY);
    record.winding = started;
    return started;
  }

  /**
   * The enemy's telegraph lands (the wire `telegraphResolve`). When a motion clip
   * carried this wind-up, it has ALREADY presented the strike — it is at contact
   * now, or it finished a beat early (the mixer reports completion one fade ahead,
   * and a shared clip is authored to its attacks' MEDIAN wind-up), or travel cut
   * it (movement wins) — so nothing is replayed: a second, generic strike after
   * the authored one would read as a double hit. Without a motion clip the generic
   * `attack` strikes now, the pre-AD-636 presentation. Returns whether a strike is
   * on screen.
   */
  landAttack(entityId: number): boolean {
    const record = this.records.get(entityId);
    if (this.animator === null || record === undefined) return false;
    if (record.winding) {
      record.winding = false;
      return this.animator.activeAction(entityId) === 'attack';
    }
    return this.animator.playAction(entityId, 'attack');
  }

  /** attackId → motion via the entity's def (by display name), or `null`. */
  private attackMotion(name: string | null | undefined, attackId: string): string | null {
    if (name == null || name === '') return null;
    this.dropStaleMaps();
    if (this.attackMotionMap === null) {
      const enemies = this.content.enemies();
      if (enemies.length === 0) return null;
      this.attackMotionMap = buildEnemyAttackMotionMap(enemies);
    }
    return this.attackMotionMap.get(name)?.get(attackId) ?? null;
  }

  /**
   * Advance every creature mixer and age the corpses. `dtMs` is MILLISECONDS; `isHeld` pauses
   * the creatures it names for a hit-stop beat (`RigAnimator.update`).
   */
  update(dtMs: number, isHeld?: (entityId: number) => boolean): void {
    this.animator?.update(dtMs, isHeld);
    // Upgrade every enemy that arrived before the pack did, once the pack has enemies. One map
    // size check per frame while nothing is waiting; ensureNameMap is O(1) once built.
    if (
      this.awaitingContent.size > 0 &&
      !this.disposed &&
      !this.held &&
      this.ensureNameMap() !== null
    ) {
      const waiting = [...this.awaitingContent];
      this.awaitingContent.clear();
      for (const [entityId, pending] of waiting)
        this.upgrade(entityId, pending.group, pending.name);
    }
    if (this.corpses.length === 0) return;
    const gone: Corpse[] = [];
    for (const corpse of this.corpses) {
      corpse.remainingMs -= dtMs;
      if (corpse.remainingMs <= 0) gone.push(corpse);
    }
    if (gone.length === 0) return;
    this.corpses = this.corpses.filter((corpse) => corpse.remainingMs > 0);
    for (const corpse of gone) this.dropCorpse(corpse);
  }

  /**
   * The server removed an entity. If its proxy is animated and holding its death
   * clip, keep it as a corpse for `corpseLingerMs` and call `onGone` when it is
   * dropped (the caller then removes the group from the scene); returns `true`.
   * Otherwise behaves exactly like `remove()` and returns `false` — the caller
   * removes the group now, as before.
   */
  retire(entityId: number, onGone: () => void): boolean {
    const record = this.records.get(entityId);
    if (
      this.disposed ||
      record === undefined ||
      !record.animated ||
      this.animator?.activeAction(entityId) !== 'dead'
    ) {
      this.remove(entityId);
      return false;
    }
    // A zone never reuses its entity ids, so the animator keeps this entityId key; an id
    // from ANOTHER zone's space that matches drops the corpse first (`upgrade`).
    this.records.delete(entityId);
    this.corpses.push({ entityId, record, remainingMs: this.corpseLingerMs, onGone });
    return true;
  }

  /** Corpses still lingering — test/diagnostic surface. */
  get corpseCount(): number {
    return this.corpses.length;
  }

  /**
   * The entity left/died — drop any settled proxy and guard any in-flight load. The
   * caller removes the group from the scene separately; this frees the proxy's GPU
   * resources. A no-op for an entity that was never upgraded (a cone-only enemy).
   */
  remove(entityId: number): void {
    // An enemy that left before the pack loaded must not be upgraded onto a discarded group.
    this.awaitingContent.delete(entityId);
    this.fallbackIds.delete(entityId);
    const record = this.records.get(entityId);
    if (record === undefined) return;
    this.records.delete(entityId);
    this.releaseRecord(entityId, record);
    // If still loading (object null), the generation guard drops the pending clone.
  }

  /** Detach + dispose every proxy and corpse and drop the loader caches (teardown). */
  dispose(): void {
    this.disposed = true;
    this.awaitingContent.clear();
    for (const id of [...this.records.keys()]) this.remove(id);
    const corpses = this.corpses;
    this.corpses = [];
    for (const corpse of corpses) this.dropCorpse(corpse);
    this.animator?.dispose();
    this.packLoaders.clear();
  }

  private dropCorpse(corpse: Corpse): void {
    this.releaseRecord(corpse.entityId, corpse.record);
    try {
      corpse.onGone();
    } catch (error) {
      this.onWarn(`corpse removal failed (entity ${corpse.entityId})`, error);
    }
  }

  /** Stop the mixer (no mixer outlives its proxy), then detach + dispose the proxy. */
  private releaseRecord(entityId: number, record: EntityRecord): void {
    if (record.animated) this.animator?.detach(entityId);
    if (record.object !== null) {
      record.group.remove(record.object);
      this.disposeObject(record.object);
    }
  }
}
